// Custom hook for real-time socket connections
import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore, setDisconnectSocketFn } from '@/stores/authStore';
import { API_BASE_URL, SOCKET_URL as BAKED_SOCKET } from '@/config/clientEnv';

const SOCKET_URL =
  BAKED_SOCKET ||
  API_BASE_URL.replace(/\/api$/, '') ||
  'http://localhost:2000';

// SINGLETON socket instance - shared across all components
let globalSocket: Socket | null = null;
let globalSocketUserId: string | null = null;

function getOrCreateSocket(accessToken: string, userId: string): Socket {
  // Reuse existing socket if same user
  if (globalSocket && globalSocketUserId === userId && globalSocket.connected) {
    return globalSocket;
  }
  
  // Disconnect old socket if different user
  if (globalSocket && globalSocketUserId !== userId) {
    globalSocket.disconnect();
    globalSocket = null;
  }
  
  // Create new socket only if needed
  if (!globalSocket) {
    globalSocket = io(SOCKET_URL, {
      transports: ['polling', 'websocket'],
      auth: { token: accessToken },
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      autoConnect: true,
    });
    globalSocketUserId = userId;
  }
  
  return globalSocket;
}

// Cleanup on logout
export function disconnectSocket() {
  if (globalSocket) {
    globalSocket.disconnect();
    globalSocket = null;
    globalSocketUserId = null;
  }
}

// Register the disconnect function with authStore to avoid circular import
setDisconnectSocketFn(disconnectSocket);

interface UseRealtimeOptions {
  autoConnect?: boolean;
  rooms?: string[];
}

export function useRealtime(options: UseRealtimeOptions = {}) {
  const { autoConnect = true, rooms = [] } = options;
  const { user, accessToken, isHydrated } = useAuthStore();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const hasSetupListeners = useRef(false);

  // Initialize socket connection - use singleton
  useEffect(() => {
    if (!isHydrated || !autoConnect || !user || !accessToken) return;

    const socket = getOrCreateSocket(accessToken, user.id);
    
    // Only setup listeners once per socket
    if (!hasSetupListeners.current) {
      hasSetupListeners.current = true;
      
      socket.on('connect', () => {
        setIsConnected(true);
        setConnectionError(null);
        socket.emit('authenticate', { userId: user.id, token: accessToken });
        if (rooms.length > 0) {
          socket.emit('subscribe', rooms);
        }
      });

      socket.on('disconnect', () => {
        setIsConnected(false);
      });

      socket.on('connect_error', (error) => {
        setConnectionError(error.message);
      });

      socket.on('authenticated', (data) => {
        if (data.success) {
          console.log('Socket authenticated');
        }
      });
    }
    
    // Update connection state
    setIsConnected(socket.connected);

    // Don't disconnect on unmount - socket is shared
    return () => {
      // Only cleanup listeners if component unmounts
    };
  }, [isHydrated, autoConnect, user?.id, accessToken]);

  // Subscribe to rooms
  const subscribe = useCallback((roomNames: string[]) => {
    if (globalSocket?.connected) {
      globalSocket.emit('subscribe', roomNames);
    }
  }, []);

  // Unsubscribe from rooms
  const unsubscribe = useCallback((roomNames: string[]) => {
    if (globalSocket?.connected) {
      globalSocket.emit('unsubscribe', roomNames);
    }
  }, []);

  // Listen to events
  const on = useCallback((event: string, callback: (...args: any[]) => void) => {
    globalSocket?.on(event, callback);
    return () => {
      globalSocket?.off(event, callback);
    };
  }, []);

  // Emit events
  const emit = useCallback((event: string, data?: any) => {
    if (globalSocket?.connected) {
      globalSocket.emit(event, data);
    }
  }, []);

  return {
    socket: globalSocket,
    isConnected,
    connectionError,
    subscribe,
    unsubscribe,
    on,
    emit,
  };
}

// Hook for listening to specific events
export function useRealtimeEvent<T = any>(
  event: string,
  callback: (data: T) => void,
  deps: any[] = []
) {
  const { on } = useRealtime();

  useEffect(() => {
    const unsubscribe = on(event, callback);
    return unsubscribe;
  }, [event, on, ...deps]);
}

// Hook for notifications
export function useRealtimeNotifications() {
  const [notifications, setNotifications] = useState<any[]>([]);
  const { on } = useRealtime();

  useEffect(() => {
    const unsubscribe = on('notification:new', (notification) => {
      setNotifications(prev => [notification, ...prev]);
    });
    return unsubscribe;
  }, [on]);

  const clearNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    notifications,
    clearNotification,
    clearAll,
  };
}

// Hook for typing indicators
export function useTypingIndicator(conversationId: string) {
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const { emit, on } = useRealtime();
  const typingTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    const unsubscribeTyping = on('user:typing', (data: { userId: string; conversationId: string }) => {
      if (data.conversationId === conversationId) {
        setTypingUsers(prev => {
          if (!prev.includes(data.userId)) {
            return [...prev, data.userId];
          }
          return prev;
        });

        // Auto-clear after 3 seconds
        if (typingTimeoutRef.current[data.userId]) {
          clearTimeout(typingTimeoutRef.current[data.userId]);
        }
        typingTimeoutRef.current[data.userId] = setTimeout(() => {
          setTypingUsers(prev => prev.filter(id => id !== data.userId));
        }, 3000);
      }
    });

    const unsubscribeStopped = on('user:stopped-typing', (data: { userId: string; conversationId: string }) => {
      if (data.conversationId === conversationId) {
        setTypingUsers(prev => prev.filter(id => id !== data.userId));
        if (typingTimeoutRef.current[data.userId]) {
          clearTimeout(typingTimeoutRef.current[data.userId]);
        }
      }
    });

    return () => {
      unsubscribeTyping();
      unsubscribeStopped();
      Object.values(typingTimeoutRef.current).forEach(clearTimeout);
    };
  }, [conversationId, on]);

  const startTyping = useCallback(() => {
    emit('typing:start', { conversationId });
  }, [emit, conversationId]);

  const stopTyping = useCallback(() => {
    emit('typing:stop', { conversationId });
  }, [emit, conversationId]);

  return {
    typingUsers,
    startTyping,
    stopTyping,
  };
}

// Hook for real-time messages
export function useRealtimeMessages(conversationId: string) {
  const [messages, setMessages] = useState<any[]>([]);
  const { on, subscribe, unsubscribe } = useRealtime();

  useEffect(() => {
    const room = `conversation:${conversationId}`;
    subscribe([room]);

    const unsubscribeNew = on('message:new', (message) => {
      setMessages(prev => [...prev, message]);
    });

    const unsubscribeRead = on('message:read', (data) => {
      setMessages(prev => 
        prev.map(m => m.id === data.messageId ? { ...m, readAt: data.readAt } : m)
      );
    });

    return () => {
      unsubscribe([room]);
      unsubscribeNew();
      unsubscribeRead();
    };
  }, [conversationId, subscribe, unsubscribe, on]);

  return messages;
}

export default useRealtime;
